import { Icon, Select } from "@/design-system";

const SORT_OPTIONS = [
  { value: "", label: "Sort: Relevance" },
  { value: "asc", label: "Price: low to high" },
  { value: "desc", label: "Price: high to low" },
];

const VIEWS = [
  { value: "grid", icon: "grid_view", label: "Grid view" },
  { value: "list", icon: "view_list", label: "List view" },
];

/**
 * Segmented icon toggle.
 *
 * The literal 34px / 3px here are deliberate, copied from the reference's `vbtn()`
 * helper and its track markup. The handoff calls these out as documented one-offs
 * ("odd-pixel paddings inside components"); don't "improve" them into
 * `--control-height-sm` (now 30px) or `--space-1` — that changes the design.
 */
function ViewToggle({ view, onViewChange }) {
  return (
    <div className="flex shrink-0 gap-[3px] rounded-md bg-neutral-100 px-[3px] py-[3px]">
      {VIEWS.map((v) => {
        const active = view === v.value;
        return (
          <button
            key={v.value}
            type="button"
            onClick={() => onViewChange?.(v.value)}
            aria-label={v.label}
            aria-pressed={active}
            className="inline-flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-sm border-none [transition:background_var(--duration-fast),color_var(--duration-fast)]"
            style={{
              background: active ? "var(--surface-card)" : "transparent",
              color: active ? "var(--blue-700)" : "var(--text-tertiary)",
              boxShadow: active ? "var(--shadow-sm)" : "none",
            }}
          >
            <Icon name={v.icon} size={20} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Listing toolbar — result range, grid/list toggle, per-page and sort selects.
 *
 * Ported from the reference storefront's listing toolbar
 * (`Dens N Dente Tailwind.dc.html`, the `Showing … of … products` block), which
 * fixes the control order (per-page before sort) and the copy (a **range**,
 * `1–12 of 11,747`, not a bare count).
 *
 * One deliberate deviation from the reference: it stacks below `sm`. The reference
 * keeps a single `shrink-0` row, which overflows a 390px viewport.
 *
 * `onSortChange` / `onPerPageChange` still receive a DOM-event-shaped argument
 * (`{ target: { value } }`) even though `Select` emits a bare value — that keeps
 * both call sites, and the `perPage` state feeding the products cache key, the
 * fetch limit and `totalPages`, completely unchanged. Normalising the chain to
 * plain values is tracked in the skill backlog.
 *
 * `page` and `shown` drive the range: `shown` is the number of rows actually
 * rendered (pass `products.length`), because the last page is short and the API
 * can return fewer rows than asked for.
 *
 * `loading` keeps the toolbar mounted through a page change instead of letting it
 * unmount — BEHAVIOR.md only swaps the grid for a skeleton, the chrome stays put.
 * The products cache is keyed by page, so `shown` collapses to 0 the instant the
 * key changes; while loading the range is derived from `page`/`perPage`/`total`
 * instead, which is exactly what the reference does (its data is synchronous).
 */
export default function ProductToolbar({
  page = 1,
  shown = 0,
  perPageOptions = [12, 24, 48],
  onPerPageChange,
  perPage = 12,
  sort = "",
  onSortChange,
  total = 0,
  view = "grid",
  onViewChange,
  showPerPage = true,
  showSort = true,
  showViewToggle = true,
  loading = false,
}) {
  const rangeStart = (page - 1) * perPage + 1;
  const expected = loading
    ? Math.max(0, Math.min(perPage, total - (page - 1) * perPage))
    : shown;
  const rangeEnd = Math.min(rangeStart + expected - 1, total);
  const range = expected
    ? `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()}`
    : "0";

  return (
    <div className="mb-[var(--space-5)] flex flex-col gap-[var(--space-3)] sm:min-h-[44px] sm:flex-row sm:items-center sm:justify-between sm:gap-[var(--space-4)]">
      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        <span className="text-sm text-ink-2">
          Showing {range} of {total.toLocaleString()} products
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-3)] sm:shrink-0">
        {showViewToggle && (
          <ViewToggle view={view} onViewChange={onViewChange} />
        )}

        {showPerPage && (
          <div
            className="select-pill min-w-[8rem] flex-1 sm:max-w-[150px]"
            role="group"
            aria-label="Products per page"
          >
            <Select
              value={String(perPage)}
              onChange={(value) => onPerPageChange?.({ target: { value } })}
              options={perPageOptions.map((count) => ({
                value: String(count),
                label: `${count} per page`,
              }))}
            />
          </div>
        )}

        {showSort && (
          <div
            className="select-pill min-w-[12rem] flex-1 sm:max-w-[220px]"
            role="group"
            aria-label="Sort products"
          >
            <Select
              value={sort}
              onChange={(value) => onSortChange?.({ target: { value } })}
              options={SORT_OPTIONS}
            />
          </div>
        )}
      </div>
    </div>
  );
}
